<template>
  <section class="chart">
    <div class="chart__inner">
      <div class="ag-grid-wrapper">
        <h2 class="chart__header">Deer Management Statistics</h2>
        <grid-legend :cols="columnDefs" :grid-column-api="gridColumnApi">
          <button class="chart-control" @click.prevent="_export">
            Export Data
          </button>
        </grid-legend>
        <ag-grid-vue
          class="ag-theme-alpine"
          style="height: 750px"
          :default-col-def="defaultColDef"
          :column-defs="columnDefs"
          :row-data="rows"
          @grid-ready="onGridReady"
          @filter-changed="onUpdated"
          @sort-changed="onUpdated"
        >
        </ag-grid-vue>
      </div>
    </div>
  </section>
</template>

<script>
import consola from "consola";
import XLSX from "xlsx";
import { AgGridVue } from "ag-grid-vue";
import Utils from "~/lib/Utils";
import GridLegend from "~/components/visualization/GridLegend";

export default {
  name: "App",
  components: {
    AgGridVue,
    GridLegend,
  },
  async asyncData({ $axios, error }) {
    try {
      const { data } = await $axios
        .get("/api/chart/data")
        .catch((err) => error({ statusCode: 500, message: err.message }));
      const cols = Object.keys(data.rows[0]);
      const rows = data.rows;
      return { cols, rows };
    } catch (err) {
      consola.error(err.message);
      error({ statusCode: err.statusCode, message: err.message });
    }
  },
  data() {
    return {
      columnDefs: [],
      rowData: null,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          // pass in additional parameters to the text filter
          buttons: ["reset", "apply"],
          closeOnApply: true,
        },
      },
      gridApi: null,
      gridColumnApi: null,
    };
  },
  beforeMount() {
    this.cols.forEach((c) => {
      if (c === "county") {
        this.columnDefs.push({
          field: c,
          minWidth: 200,
          width: 200,
          headerTooltip: "County",
          resizable: false,
          suppressSizeToFit: true,
          filter: true,
          filterParams: {
            // pass in additional parameters to the number filter
            buttons: ["reset", "apply"],
            closeOnApply: true,
          },
        });
      } else if (c === "year") {
        this.columnDefs.push({
          field: c,
          minWidth: 100,
          width: 100,
          resizable: false,
          suppressSizeToFit: true,
          headerTooltip: "Year",
          filterParams: {
            // pass in additional parameters to the number filter
            buttons: ["reset", "apply"],
            closeOnApply: true,
          },
        });
      } else {
        this.columnDefs.push({
          field: c,
          width: 85,
          resizable: true,
          type: "rightAligned",
          headerTooltip: Utils.gridColumnDescription(c),
          filter: "agNumberColumnFilter",
          filterParams: {
            // pass in additional parameters to the number filter
            buttons: ["reset", "apply"],
            debounceMs: 200,
            closeOnApply: true,
          },
        });
      }
    });
  },
  mounted() {
    window.V = this;
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      // this.gridApi.sizeColumnsToFit();
      this.gridColumnApi = params.columnApi;
    },
    onUpdated(params) {
      consola.info("filtered");
      this.gridColumnApi.autoSizeAllColumns();
    },
    _export() {
      const gridData = this.gridApi
        .getDataAsCsv()
        .split("\r\n")
        .map((r) => r.replace(/"/g, "").split(","));
      // consola.info(gridData);

      const ws = XLSX.utils.aoa_to_sheet(gridData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
      /* generate file and send to client */
      XLSX.writeFile(wb, "Deer Harvest Data.xlsx");
    },
  },
};
</script>

<style lang="scss">
@import "ag-grid-community/src/styles/ag-grid.scss";
// @import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
@import "ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin.scss";
.chart {
  width: 100%;
  @include tablet() {
    width: 80%;
  }
  margin: auto;
  margin-top: 1rem;
  &__computed {
    padding-left: 0;
    color: black;
  }
  &__inner {
    background-color: white;
    height: 100%;
    padding: 0 1rem;
    &--no-safari {
      position: relative;
    }
  }
  &__header {
    font-family: "Rockwell W01", serif;
    font-size: 24px;
    text-transform: uppercase;
    margin: 4px 0 20px 0;
  }

  %chart_global {
    min-height: 440px;
    margin: auto;
    margin-bottom: 1rem;
    clear: both;
  }

  &-control {
    background-color: #33a5b8;
    cursor: pointer;
    font-family: "Gill Sans W03", sans-serif;
    font-weight: 700;
    border: 0;
    border-radius: 10px;
    padding: 3px 8px;
    text-transform: uppercase;
    margin-right: 1rem;
    color: white;
    &:hover {
      background-color: #5d3021;
    }
  }
}

.ag-grid-wrapper {
  font-family: "Gill Sans W03", sans-serif;
  padding: 1rem;
  min-height: 250px;
  height: 100%;
}

.ag-theme-alpine {
  margin-top: 2rem;
  .ag-header-cell-label {
    font-family: "Gill Sans W03", sans-serif;
    font-weight: 700;
  }
  .ag-cell {
    font-family: "Gill Sans W03", sans-serif;
  }
}
.ag-theme-alpine {
  @include ag-theme-alpine(
    (
      odd-row-background-color: #f1f0e4,
      cell-horizontal-border: solid ag-derived(secondary-border-color),
    )
  );
}
</style>
